import { OrderFormElementRoute, OrderFormElementType } from "enums";
import { OrderItem } from "modules/charts-shared/types";
import { Concentration, OrderElementResponse } from "modules/order-list/types";
import { getOptions } from "utils/enum-mapper";

const MEDICATION_ROUTE_REQUIRED_ORDER_TYPES: ReadonlySet<OrderFormElementType> =
  new Set(["Medication", "Contrast"]);

const ORDER_FORM_ELEMENT_ROUTE_VALUES: readonly string[] = getOptions(
  "orderFormElementRoute"
).map((option) => option.value);

function isOrderFormElementRoute(
  value: string
): value is OrderFormElementRoute {
  return ORDER_FORM_ELEMENT_ROUTE_VALUES.includes(value);
}

function requiresMedicationRoute(
  orderElementType: OrderFormElementType
): boolean {
  return MEDICATION_ROUTE_REQUIRED_ORDER_TYPES.has(orderElementType);
}

/**
 * Resolves the set of routes the order element itself allows, sourced from
 * its fixed route or its common doses. An empty result means the element
 * carries no route metadata to validate against.
 */
function getValidMedicationRoutes(
  element: Pick<
    OrderElementResponse,
    "orderFormElementRoute" | "orderFormElementCommonDoses"
  >
): OrderFormElementRoute[] {
  if (element.orderFormElementRoute) {
    return [element.orderFormElementRoute];
  }

  const routes = new Set<OrderFormElementRoute>();
  element.orderFormElementCommonDoses?.forEach((dose) => {
    if (dose.route && isOrderFormElementRoute(dose.route)) {
      routes.add(dose.route);
    }
  });

  return Array.from(routes);
}

/**
 * `validRoutes` carries an explicit typed distinction:
 * - `undefined`: no order element metadata was available to check against
 *   (e.g. a manually authored order with no catalog element). Any
 *   legitimate `OrderFormElementRoute` value is accepted since there is no
 *   catalog restriction to enforce.
 * - `[]` (empty array): the order element's metadata was resolved and the
 *   element is route-less (no fixed route, no common doses with a route).
 *   A route-required element in this state can never be completed with an
 *   arbitrary route, so this always fails.
 * - non-empty array: the selected route must be one of them (guards
 *   against a stale/manual route that no longer matches the order element,
 *   e.g. an Ally-suggested medication).
 */
function hasValidMedicationRoute(
  orderElementType: OrderFormElementType,
  route: string | null | undefined,
  validRoutes?: readonly OrderFormElementRoute[]
): boolean {
  if (!requiresMedicationRoute(orderElementType)) return true;
  if (!route || !isOrderFormElementRoute(route)) return false;
  if (validRoutes === undefined) return true;

  return validRoutes.length > 0 && validRoutes.includes(route);
}

/**
 * `OrderItem` (order queue / order set cart entries) doesn't carry the order
 * element's route metadata (`orderFormElementRoute` /
 * `orderFormElementCommonDoses`), so this only checks that a route is
 * present, not that it belongs to the specific medication. Full
 * per-medication membership validation happens where that metadata is
 * available: dropdown filtering in CommonDoses/MyFavoriteDoses and submit
 * revalidation in AddMedicationOrderModal.
 */
function hasValidOrderItemRoute(
  item: Pick<OrderItem, "orderElementType" | "route" | "orderElementRoute">
): boolean {
  return hasValidMedicationRoute(
    item.orderElementType,
    item.orderElementRoute ?? item.route
  );
}

function calculateVolumetricMedicationAmount(
  doseAmount: number | string,
  weight: number | string
) {
  return Number((Number(doseAmount) * Number(weight)).toFixed(2));
}

function calculateMedicationAmount(
  concentration: Partial<Concentration>,
  doseAmount: number | string,
  doseUnit: string,
  weight: number | string
) {
  let dosage = Number(doseAmount);
  let concentrationAmount = Number(concentration?.doseAmount || 1);
  let concentrationVolume = Number(concentration?.volumeAmount || 1);
  const numericWeight = Number(weight);

  dosage = getAmountInMg(dosage, doseUnit);
  concentrationAmount = getAmountInMg(
    concentrationAmount,
    concentration.doseUnit ?? ""
  );
  concentrationVolume = getVolumeInMl(
    concentrationVolume,
    concentration.volumeUnit ?? ""
  );

  const concentrationByMl = concentrationAmount / concentrationVolume;
  return Number(
    Number((dosage / concentrationByMl) * numericWeight).toFixed(2)
  );
}

function calculateMedicationAmountMLKG(
  doseAmount: number,
  doseUnit: string,
  weight: number
) {
  const dosage = getAmountInMg(doseAmount, doseUnit);

  return Number(dosage * weight);
}

function getAmountInMg(amount: number | string, unit: string) {
  let numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) {
    return 0;
  }

  if (/\bmcg\b/.test(unit)) {
    numericAmount *= 0.001;
  } else if (/\bg\b/.test(unit)) {
    numericAmount *= 1000;
  }

  return Number(numericAmount.toFixed(2));
}

function getVolumeInMl(volume: number | string, unit: string) {
  let numericVolume = Number(volume);
  if (Number.isNaN(numericVolume)) {
    return 0;
  }

  if (unit === "L") {
    numericVolume *= 1000;
  }

  return Number(numericVolume.toFixed(2));
}

export {
  calculateMedicationAmount,
  getAmountInMg,
  getVolumeInMl,
  calculateMedicationAmountMLKG,
  calculateVolumetricMedicationAmount,
  requiresMedicationRoute,
  getValidMedicationRoutes,
  isOrderFormElementRoute,
  hasValidMedicationRoute,
  hasValidOrderItemRoute,
};
